import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import {
  useGetFacilityUserDetailsById,
  useGetFacilityUserMetaData,
  useUnBlockFacilityUser,
} from '../../API/hooks/FacilityUserHook';
import facilityUserInitialValues from './facilityUserInitialValues';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import {
  FACILITY_NEW_USER_ROUTE_PATH,
  FACILITY_USER_ROUTE_PATH,
} from '../../routers/routePaths';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import Visibility from '../../../../../../packages/shared_lib/src/assets/images/eye.svg';
import VisibilityOff from '../../../../../../packages/shared_lib/src/assets/images/eye-off.svg';
import { hasRole } from '../login/storageUtility';
import './facility.css';
import LockedAccountAlert from '../components/AccountLockReset/LockedAccountAlert';
const FacilityUserView: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { facilityUserId } = useParams<{ facilityUserId?: string }>();
  const [initialValues, setInitialValues] = useState(facilityUserInitialValues);
  const { facilityUserMetaData } = useGetFacilityUserMetaData(userId);

  const { FacilityUserDetailsById, isLoading } = useGetFacilityUserDetailsById(
    userId,
    Number(facilityUserId)
  );

  const { mutate: unBlockFacilityUser } = useUnBlockFacilityUser(() => {
    window.location.reload();
  });

  const facilityUserddlId = facilityUserMetaData?.roles?.find(
    (item) => item.name.toLocaleLowerCase() === 'facility user'
  )?.id;

  const caseManagerddlId = facilityUserMetaData?.roles?.find(
    (item) => item.name.toLocaleLowerCase() === 'case manager'
  )?.id;

  useEffect(() => {
    if (FacilityUserDetailsById) {
      setInitialValues({
        id: FacilityUserDetailsById.id,
        firstName: FacilityUserDetailsById.firstName,
        lastName: FacilityUserDetailsById.lastName,
        primaryEmail: FacilityUserDetailsById.primaryEmail,
        username: FacilityUserDetailsById.username,
        roleId: FacilityUserDetailsById.roleId,
        facilityId: FacilityUserDetailsById.facilityId,
        managedFacilityIds: FacilityUserDetailsById.managedFacilityIds,
        status: FacilityUserDetailsById.status,
        password: FacilityUserDetailsById.password,
        notes: FacilityUserDetailsById.notes,
        mfaEnabledByAdmin: FacilityUserDetailsById.mfaEnabledByAdmin,
        isLocked: FacilityUserDetailsById.isLocked,
      });
    }
  }, [FacilityUserDetailsById]);

  const handleEditFacilityUserInfo = (facilityUserId: number) => {
    navigate(FACILITY_NEW_USER_ROUTE_PATH + `/${facilityUserId}`);
  };
  const [show, setShow] = useState(false);
  const getShowPwdIcon = (showPassword: boolean) =>
    showPassword ? <Visibility /> : <VisibilityOff />;
  const togglePasswordVisibility = () => {
    setShow((prev) => !prev);
  };

  const handleUnLockFacilityUser = (
    currentUserId: string,
    facilityUserId: number
  ) => {
    unBlockFacilityUser({
      userId: currentUserId,
      facilityUserId,
    });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }
  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className=" flex-justify-space-between align-items-baseline pb-24">
              <Box className="text-align-left display-flex">
                <CustomIconButton
                  id="back-button"
                  icon={<BackIcon />}
                  onClick={() => {
                    navigate(FACILITY_USER_ROUTE_PATH);
                  }}
                ></CustomIconButton>

                <CustomTypography
                  className="pl-8"
                  variant="h4"
                  id="add-new-facility-user-text"
                >
                  View Facility User
                </CustomTypography>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handleEditFacilityUserInfo(initialValues.id);
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
        <LockedAccountAlert
          isLocked={initialValues.isLocked}
          userId={userId}
          targetUserId={initialValues.id}
          onUnlock={handleUnLockFacilityUser}
        />
        <Box className="form-background p-24">
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="user-info-text"
              >
                User Information
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Box className="display-flex">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="first-name-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  First Name
                </CustomTypography>
                <CustomTypography
                  id="first-name-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {initialValues.firstName ?? 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="last-name-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Last Name
                </CustomTypography>
                <CustomTypography
                  id="last-name-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {initialValues.lastName ?? 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="user-name-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Username
                </CustomTypography>
                <CustomTypography
                  id="user-name-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {initialValues.username ?? 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="primary-email-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Primary Email
                </CustomTypography>
                <CustomTypography
                  id="primary-email-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {initialValues.primaryEmail ?? 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="role-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Role
                </CustomTypography>
                <CustomTypography
                  id="role-label"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left pb-12"
                >
                  {facilityUserMetaData?.roles.find(
                    (item) => item.id === initialValues.roleId
                  )?.name ?? 'N/A'}
                </CustomTypography>
              </Grid2>
              {initialValues.roleId === Number(facilityUserddlId) && (
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-staff-facility-name-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Facility Name
                  </CustomTypography>
                  <CustomTypography
                    id="facility-name-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left pb-12"
                  >
                    {facilityUserMetaData?.activeFacilities.find(
                      (facility) => facility.id === initialValues.facilityId
                    )?.name ?? 'N/A'}
                  </CustomTypography>
                </Grid2>
              )}
              {initialValues.roleId === Number(caseManagerddlId) && (
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="case-manager-managed-facilities-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Managed Facilities
                  </CustomTypography>
                  {initialValues.managedFacilityIds.length > 0 ? (
                    <CustomTypography
                      id="case-manager-managed-facilities-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.managedFacilityIds
                        .map((item) => item.name)
                        .join(', ') ?? 'N/A'}
                    </CustomTypography>
                  ) : null}
                </Grid2>
              )}
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="facility-user-status-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Status
                </CustomTypography>
                <Box className="text-align-left">
                  {initialValues.status ? (
                    <Box className="display-flex align-items-baseline">
                      <Box component="span" className="status-active-dot " />
                      <CustomTypography
                        id="facility-user-status-value-active"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pl-4"
                      >
                        {' '}
                        Active
                      </CustomTypography>
                    </Box>
                  ) : (
                    <Box className="display-flex align-items-baseline">
                      <Box component="span" className="status-inactive-dot " />
                      <CustomTypography
                        id="facility-user-status-value-inactive"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pl-4"
                      >
                        {' '}
                        Inactive
                      </CustomTypography>
                    </Box>
                  )}
                </Box>
              </Grid2>
              {initialValues.roleId === Number(caseManagerddlId) && (
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-user-2fa-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Two Factor Authentication
                  </CustomTypography>
                  <Box className="text-align-left">
                    {initialValues.mfaEnabledByAdmin ? (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-active-dot" />
                        <CustomTypography
                          id="facility-user-2fa-value-active"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          On
                        </CustomTypography>
                      </Box>
                    ) : (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-inactive-dot" />
                        <CustomTypography
                          id="facility-user-2fa-value-inactive"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          Off
                        </CustomTypography>
                      </Box>
                    )}
                  </Box>
                </Grid2>
              )}
              {initialValues.roleId === Number(facilityUserddlId) && (
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="password-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-8"
                  >
                    Password
                  </CustomTypography>
                  <Box className="display-flex align-items-center">
                    <CustomTypography
                      id="password-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {show
                        ? (initialValues.password ?? '')
                        : '•'.repeat((initialValues.password ?? '').length)}
                    </CustomTypography>
                    <CustomIconButton
                      id={`toggle-password`}
                      onClick={togglePasswordVisibility}
                      size="small"
                      icon={getShowPwdIcon(show)}
                      className="ml-8"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
          <Box className="pt-32">
            <CustomDivider />
          </Box>
          <Box className="pt-32">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="facility-user-notes-text"
                >
                  Facility User Notes
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                <CustomTypography
                  id="notes-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Notes
                </CustomTypography>
                <CustomTypography
                  id="notes-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {initialValues.notes ?? 'N/A'}
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default FacilityUserView;
